/**
 * `createApiSubscriptionsSource` — the subscriptions-mgmt-api port over the
 * shared `ApiClient`.
 *
 * Backs the Settings "Billing" tab: reads the caller's current subscription (the
 * billing/entitlement anchor) so the UI can reflect the plan tier, lifecycle
 * status and any per-subscription overrides. The wire is snake_case (the commons
 * `Subscription` model serialized); this adapter maps it to the camelCase summary
 * the UI renders. The canonical `/subscriptions` path is rewritten onto the
 * `.management.` zone (`/management/subscriptions-mgmt-api/v0/...`) by the
 * dashboard `rewritePath`, so region routing / auth / envelope unwrap are
 * inherited.
 *
 * Reads are pinned to the caller's own customer server-side (external callers
 * cannot pass an arbitrary `customer_id`), so no customer id is sent from here.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asNumber, asRecord, asString, rowsFromWire } from "./wire";

/** Lifecycle status mirrored from the commons `SubscriptionStatus` enum. */
export type SubscriptionStatus =
  | "incomplete"
  | "trialing"
  | "active"
  | "past_due"
  | "paused"
  | "canceled"
  | "expired";

/** Statuses that still grant the plan's entitlements/quota (active-ish). */
const ENTITLED_STATUSES: ReadonlySet<SubscriptionStatus> = new Set<SubscriptionStatus>([
  "trialing",
  "active",
  "past_due",
]);

export function isEntitledStatus(status: SubscriptionStatus | null | undefined): boolean {
  return status != null && ENTITLED_STATUSES.has(status);
}

export interface SubscriptionSummary {
  id: string;
  /** The plan this subscription grants (`free` / `pro` / `enterprise` / custom). */
  planId: string;
  status: SubscriptionStatus;
  displayName: string | null;
  /** Seat / unit count for per-seat plans. */
  quantity: number;
  currency: string | null;
  /** Epoch seconds. */
  trialEnd: number | null;
  currentPeriodEnd: number | null;
  canceledAt: number | null;
  /** When true the subscription ends at `currentPeriodEnd` instead of renewing. */
  cancelAtPeriodEnd: boolean;
  /** Sparse overrides layered over the resolved plan (display-only here). */
  entitlementOverrides: Record<string, unknown>;
  quotaOverrides: Record<string, unknown>;
  /**
   * The row's own metadata bag — where a per-customer `load_profile` lives.
   *
   * Distinct from `entitlementOverrides`: that says which FEATURES this
   * customer has, this carries knobs that are not features. Both already
   * existed on the backend model; only the entitlement half was on the wire.
   */
  metadata: Record<string, unknown>;
  /** Epoch seconds the row was created (used to pick the newest subscription). */
  createdAt: number | null;
}

export interface SubscriptionsSource {
  /**
   * The caller's current subscription — the newest entitled (active-ish) row,
   * falling back to the newest row overall, or `null` when the customer holds
   * none (the UI then reflects the default free tier).
   */
  getCurrentSubscription(): Promise<SubscriptionSummary | null>;
}

function subscriptionFromWire(raw: Record<string, unknown>): SubscriptionSummary {
  return {
    id: asString(raw.id),
    planId: asString(raw.plan_id, "free"),
    status: (raw.status ?? "active") as SubscriptionStatus,
    displayName: asNullableString(raw.display_name),
    quantity: asNumber(raw.quantity) ?? 1,
    currency: asNullableString(raw.currency),
    trialEnd: asNumber(raw.trial_end),
    currentPeriodEnd: asNumber(raw.current_period_end),
    canceledAt: asNumber(raw.canceled_at),
    cancelAtPeriodEnd: Boolean(raw.cancel_at_period_end ?? false),
    entitlementOverrides: asRecord(raw.entitlement_overrides),
    quotaOverrides: asRecord(raw.quota_overrides),
    metadata: asRecord(raw.metadata),
    createdAt: asNumber(raw.created_at),
  };
}

/** Newest-first by `createdAt` (missing timestamps sort last). */
function byNewest(a: SubscriptionSummary, b: SubscriptionSummary): number {
  return (b.createdAt ?? 0) - (a.createdAt ?? 0);
}

export function createApiSubscriptionsSource(client: ApiClient): SubscriptionsSource {
  return {
    async getCurrentSubscription(): Promise<SubscriptionSummary | null> {
      const res = await client.get<unknown>("/subscriptions");
      const rows = rowsFromWire(unwrapData(res)).map(subscriptionFromWire).sort(byNewest);
      return rows.find((s) => isEntitledStatus(s.status)) ?? rows[0] ?? null;
    },
  };
}
